(() => {
  if (customElements.get('dragon-tree-page')) return;

  class DragonTreePage extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.galleryResizeObserver?.disconnect();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = this.querySelector('.db-form') || this.querySelector('form');
      if (!this.form) return;
      this.quantity = this.form.querySelector('[name="quantity"]');
      this.mainButton = this.querySelector('[data-main-submit]');
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      this.sticky = this.querySelector('[data-sticky]');
      this.status = this.querySelector('[data-status]');
      this.pending = false;
      this.moneyFormat = new Intl.NumberFormat(this.dataset.locale || 'en-CA', {
        style: 'currency', currency: this.dataset.currency || 'CAD', currencyDisplay: 'narrowSymbol'
      });
      this.form.addEventListener('change', () => this.update(), { signal });
      this.quantity?.addEventListener('input', () => this.update(), { signal });
      this.querySelectorAll('[data-quantity-change]').forEach(button => {
        button.addEventListener('click', () => {
          if (!this.quantity) return;
          this.quantity.value = String(Math.min(10, Math.max(1, this.getQuantity() + Number(button.dataset.quantityChange))));
          this.update();
        }, { signal });
      });
      this.form.addEventListener('submit', event => this.addToCart(event), { signal });
      this.stickyButton?.addEventListener('click', () => {
        if (this.pending || this.mainButton?.disabled) return;
        if (this.mainButton) this.form.requestSubmit(this.mainButton);
        else this.form.requestSubmit();
      }, { signal });

      this.setupGallery(signal);
      this.setupReviews(signal);
      if ('IntersectionObserver' in window && this.sticky && this.mainButton) {
        this.observer = new IntersectionObserver(([entry]) => {
          const visible = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.sticky.hidden = !visible;
          document.body.classList.toggle('db-sticky-visible', visible);
        });
        this.observer.observe(this.mainButton);
      }
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.galleryResizeObserver?.disconnect();
      if (this.galleryFrame) cancelAnimationFrame(this.galleryFrame);
      this.galleryFrame = null;
      document.body.classList.remove('db-sticky-visible');
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    setupGallery(signal) {
      const track = this.querySelector('[data-gallery-track]');
      if (!track) return;
      const slides = [...track.querySelectorAll('.db-gallery-slide')];
      const dots = [...this.querySelectorAll('[data-gallery-dot]')];
      if (!slides.length) return;
      const slideLeft = slide => slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      const updateGallery = () => {
        this.galleryFrame = null;
        if (!this.isConnected) return;
        let nearest = 0;
        let distance = Infinity;
        slides.forEach((slide, index) => {
          const nextDistance = Math.abs(slideLeft(slide) - track.scrollLeft);
          if (nextDistance < distance) {
            nearest = index;
            distance = nextDistance;
          }
        });
        dots.forEach(dot => {
          const active = Number(dot.dataset.galleryDot) === nearest;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', String(active));
        });
      };
      const queueUpdate = () => {
        if (this.galleryFrame) return;
        this.galleryFrame = requestAnimationFrame(updateGallery);
      };
      dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const slide = slides[Number(dot.dataset.galleryDot)];
          if (slide) track.scrollTo({ left: slideLeft(slide), behavior: this.scrollBehavior() });
        }, { signal });
      });
      track.addEventListener('scroll', queueUpdate, { signal, passive: true });
      track.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const active = dots.findIndex(dot => dot.getAttribute('aria-current') === 'true');
        const next = Math.min(slides.length - 1, Math.max(0, Math.max(0, active) + (event.key === 'ArrowRight' ? 1 : -1)));
        track.scrollTo({ left: slideLeft(slides[next]), behavior: this.scrollBehavior() });
      }, { signal });
      window.addEventListener('resize', queueUpdate, { signal, passive: true });
      if ('ResizeObserver' in window) {
        this.galleryResizeObserver = new ResizeObserver(queueUpdate);
        this.galleryResizeObserver.observe(track);
      }
      queueUpdate();
    }

    setupReviews(signal) {
      const carousel = this.querySelector('[data-review-carousel]');
      if (!carousel) return;
      const cards = [...carousel.querySelectorAll('.db-review-card')];
      const controls = [...this.querySelectorAll('[data-review-direction]')];
      const position = this.querySelector('[data-review-position]');
      const step = () => cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : carousel.clientWidth;
      const updateControls = () => {
        if (!cards.length) return;
        const index = Math.min(cards.length - 1, Math.max(0, Math.round(carousel.scrollLeft / Math.max(1, step()))));
        if (position) position.textContent = (index + 1) + ' / ' + cards.length;
        const end = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
        controls.forEach(button => {
          button.disabled = Number(button.dataset.reviewDirection) < 0 ? carousel.scrollLeft <= 2 : carousel.scrollLeft >= end - 2;
        });
      };
      controls.forEach(button => {
        button.addEventListener('click', () => {
          carousel.scrollBy({ left: Number(button.dataset.reviewDirection) * step(), behavior: this.scrollBehavior() });
        }, { signal });
      });
      carousel.addEventListener('scroll', updateControls, { signal, passive: true });
      carousel.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        controls.find(button => Number(button.dataset.reviewDirection) === direction)?.click();
      }, { signal });
      window.addEventListener('resize', updateControls, { signal, passive: true });
      requestAnimationFrame(updateControls);
    }

    getQuantity() {
      return Math.min(10, Math.max(1, Math.floor(Number(this.quantity?.value) || 1)));
    }

    getSelected() {
      return this.form.querySelector('input[name="id"]:checked') || this.form.querySelector('input[name="id"][type="hidden"]');
    }

    getTotal() {
      const selected = this.getSelected();
      let total = Number(selected?.dataset.variantPrice || 0) * this.getQuantity();
      this.form.querySelectorAll('[data-addon-id]:checked').forEach(input => {
        total += Number(input.dataset.addonPrice) * (input.hasAttribute('data-single-addon') ? 1 : this.getQuantity());
      });
      return total;
    }

    setButtonLabel(label) {
      const mainLabel = this.querySelector('[data-button-label]');
      if (mainLabel) mainLabel.textContent = label;
      if (this.stickyButton) this.stickyButton.textContent = label;
    }

    update() {
      if (this.pending) return;
      const selected = this.getSelected();
      const available = Boolean(selected && selected.dataset.variantAvailable === 'true');
      const money = this.moneyFormat.format(this.getTotal() / 100);
      this.querySelectorAll('[data-total],[data-sticky-total]').forEach(el => el.textContent = money);
      const title = this.querySelector('[data-sticky-variant]');
      if (title) title.textContent = selected?.dataset.variantTitle || '';
      if (this.mainButton) this.mainButton.disabled = !available;
      if (this.stickyButton) this.stickyButton.disabled = !available;
      this.setButtonLabel(available ? 'Preorder' : 'Sold out');
      const decrease = this.querySelector('[data-quantity-change="-1"]');
      const increase = this.querySelector('[data-quantity-change="1"]');
      if (decrease) decrease.disabled = this.getQuantity() <= 1;
      if (increase) increase.disabled = this.getQuantity() >= 10;
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.pending || !this.form.reportValidity()) return;
      const selected = this.getSelected();
      if (!selected || selected.dataset.variantAvailable !== 'true') return;
      const quantity = this.getQuantity();
      if (this.quantity) this.quantity.value = String(quantity);
      const properties = {};
      this.form.querySelectorAll('input[name^="properties["]').forEach(input => {
        if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
        properties[input.name.slice(11, -1)] = input.value;
      });
      const items = [{ id: Number(selected.value), quantity, properties }];
      this.form.querySelectorAll('[data-addon-id]:checked').forEach(input => {
        items.push({
          id: Number(input.dataset.addonId),
          quantity: input.hasAttribute('data-single-addon') ? 1 : quantity,
          properties: { 'For tree': selected.dataset.variantTitle }
        });
      });
      this.pending = true;
      if (this.status) this.status.textContent = '';
      this.disabledStates = [...this.form.querySelectorAll('input,button')].map(el => [el, el.disabled]);
      this.disabledStates.forEach(([el]) => el.disabled = true);
      if (this.stickyButton) this.stickyButton.disabled = true;
      this.setButtonLabel('Adding…');
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(typeof result.description === 'string' ? result.description : 'Please check availability and try again.');
        }
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (this.status) {
          this.status.textContent = error instanceof TypeError
            ? 'We could not confirm your cart update. Check your cart before trying again. '
            : String(error.message || 'Your cart could not be updated. ');
          const link = document.createElement('a');
          link.href = this.dataset.cartUrl;
          link.textContent = ' View cart';
          link.style.textDecoration = 'underline';
          this.status.append(link);
        }
        this.pending = false;
        this.disabledStates.forEach(([el, disabled]) => el.disabled = disabled);
        this.update();
      }
    }
  }

  customElements.define('dragon-tree-page', DragonTreePage);
})();
